import { needsConfirmation } from '@contracts/users/CognitoStatus';
import Edit from '@mui/icons-material/Edit';
import LockClockIcon from '@mui/icons-material/LockClock';
import PersonOff from '@mui/icons-material/PersonOff';
import RestoreRounded from '@mui/icons-material/RestoreRounded';
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { Client } from '@src/clients/domain/Client';
import { useConfirmCognitoClientUser } from '@src/clientUser/manage/access/useConfirmCognitoClientUser';
import { useDeactivateClientUser } from '@src/clientUser/manage/access/useDeactivateClientUser';
import { useReactivateClientUser } from '@src/clientUser/manage/access/useReactivateClientUser';
import { AddClientUserModal } from '@src/clientUser/manage/add/AddClientUserModal';
import type { ClientUser } from '@src/clientUser/manage/ClientUser';
import { EditClientUserModal } from '@src/clientUser/manage/update/EditClientUserModal';
import { useClientUsers } from '@src/clientUser/manage/useClientUsers';
import { AddButton } from '@src/common/components/button/AddButton';
import { FilterableTable } from '@src/common/components/FilterableTable';
import { IconButton } from '@src/common/components/IconButton';
import { LoadingPage } from '@src/common/components/LoadingPage';
import { Stack } from '@src/common/components/Stack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ClientUsersTable = ({ client }: { client: Client }) => {
  const { t } = useTranslation();
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const openAddUserModal = () => setAddUserModalOpen(true);
  const closeAddUserModal = () => setAddUserModalOpen(false);

  const [editingUser, setEditingUser] = useState<ClientUser>();
  const openEditUserModal = (userToEdit: ClientUser) =>
    setEditingUser(userToEdit);
  const closeEditUserModal = () => setEditingUser(undefined);

  const { clientUsers, isLoading, refreshClientUsers } = useClientUsers({
    clientId: client.id,
  });

  const { confirmCognitoUser, isConfirming } = useConfirmCognitoClientUser({
    onSuccess: refreshClientUsers,
  });
  const { deactivate, isDeactivating } = useDeactivateClientUser({
    onSuccess: refreshClientUsers,
  });
  const { reactivate, isReactivating } = useReactivateClientUser({
    onSuccess: refreshClientUsers,
  });

  if (isLoading) {
    return <LoadingPage />;
  }

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('name'), flex: 1 },
    { field: 'email', headerName: t('email'), flex: 1 },
    { field: 'phone', headerName: t('phone'), flex: 1 },
    { field: 'cognitoStatus', headerName: t('status'), flex: 1 },
    {
      field: 'Actions',
      headerName: '',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: (params) => (
        <Stack direction="row" flex={1}>
          <IconButton
            onClick={() => openEditUserModal(params.row)}
            title={t('edit')}
          >
            <Edit />
          </IconButton>

          {needsConfirmation(params.row.cognitoStatus) && (
            <IconButton
              disabled={isConfirming}
              onClick={() =>
                confirmCognitoUser({
                  clientUserId: params.row.id,
                  clientId: client.id,
                })
              }
              title={t('confirm_user')}
            >
              <LockClockIcon color="error" />
            </IconButton>
          )}

          {params.row.isInactiveClientUser ? (
            <IconButton
              disabled={isReactivating}
              onClick={() =>
                reactivate({
                  clientUserId: params.row.id,
                  clientId: client.id,
                })
              }
              title={t('activate')}
            >
              <RestoreRounded color="secondary" />
            </IconButton>
          ) : (
            <IconButton
              disabled={isDeactivating}
              onClick={() =>
                deactivate({
                  clientUserId: params.row.id,
                  clientId: client.id,
                })
              }
              title={t('deactivate')}
            >
              <PersonOff color="secondary" />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];

  const rows = clientUsers.map((clientUser: ClientUser) => ({
    id: clientUser.id,
    name: clientUser.name,
    email: clientUser.email,
    phone: clientUser.phone,
    cognitoStatus: clientUser.cognitoStatus,
    isInactiveClientUser: clientUser.isInactive,
  }));

  return (
    <>
      <FilterableTable
        paginationAndSortMode="client"
        columns={columns}
        rows={rows}
        rightHeaderBlock={
          <AddButton
            label={t('clientUsers.addButton')}
            onClick={openAddUserModal}
          />
        }
      />

      <AddClientUserModal
        client={client}
        open={addUserModalOpen}
        onClose={closeAddUserModal}
        onCreated={() => {
          closeAddUserModal();
          refreshClientUsers();
        }}
      />

      <EditClientUserModal
        key={`edit-modal-${editingUser?.id}`}
        client={client}
        clientUser={editingUser}
        onClose={closeEditUserModal}
        onEdited={() => {
          closeEditUserModal();
          refreshClientUsers();
        }}
      />
    </>
  );
};
